import { useState, useEffect } from "react";
import { supabase } from "../supabaseClient";

// ─────────────────────────────────────────
//  Retry wrapper & offline detection
// ─────────────────────────────────────────
async function withRetry(fn, { retries = 2, delay = 500, label = "db" } = {}) {
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      const result = await fn();
      return result;
    } catch (err) {
      if (attempt === retries) {
        console.error(`${label}: all ${retries + 1} attempts failed`, err);
        throw err;
      }
      // Slumpad väntan: när servern hackar ska inte alla enheter försöka igen
      // i exakt samma ögonblick
      await new Promise(r => setTimeout(r, delay * Math.pow(2, attempt) * (0.5 + Math.random())));
    }
  }
}

// ─────────────────────────────────────────
//  Svarar servern?
// ─────────────────────────────────────────
// Varje återkommande hämtning rapporterar om den lyckades. Efter två fel i
// rad räknas servern som otillgänglig, och sidan visar det (ServerTroubleBanner)
// i stället för att bara stå still — annars laddar eleverna om gång på gång,
// vilket gör det svårare för servern att komma tillbaka.
const SERVER_TROUBLE_AFTER = 2;
let _failStreak = 0;
let _troubleListeners = [];
function reportServerResult(ok) {
  const before = _failStreak >= SERVER_TROUBLE_AFTER;
  _failStreak = ok ? 0 : _failStreak + 1;
  const after = _failStreak >= SERVER_TROUBLE_AFTER;
  if (before !== after) _troubleListeners.forEach(fn => fn(after));
}
function useServerTrouble() {
  const [trouble, setTrouble] = useState(_failStreak >= SERVER_TROUBLE_AFTER);
  useEffect(() => {
    const handler = v => setTrouble(v);
    _troubleListeners.push(handler);
    return () => { _troubleListeners = _troubleListeners.filter(h => h !== handler); };
  }, []);
  return trouble;
}

// ─────────────────────────────────────────
//  Återkommande hämtning med paus och backoff
// ─────────────────────────────────────────
// En hel sal med enheter som frågar servern samtidigt är tung last. Den här
// loopen frågar
//  - med ett grundintervall (kan bero på läget, t.ex. tätare nära tidsslut),
//  - slumpat ±20 %, så att enheterna inte går i takt,
//  - allt glesare när anropen misslyckas (dubbelt för varje fel, högst
//    maxInterval), så att en server som hackar inte får fler anrop,
//  - inte alls när fliken är dold, och direkt när den syns igen.
// fn ska returnera true när hämtningen lyckades, annars false (eller kasta).
// report: false för hämtningar som inte säger något om serverns hälsa (de
// räknas då inte in i rutan "servern svarar inte"). immediate: kör första
// hämtningen direkt (t.ex. när en sovande flik vaknar).
function startPolling(fn, { interval, maxInterval = 60000, jitter = 0.2, report = true, immediate = false } = {}) {
  const baseOf = () => (typeof interval === "function" ? interval() : interval);
  let failures = 0;
  let timer = null;
  let stopped = false;
  let running = false;

  const hidden = () => typeof document !== "undefined" && document.visibilityState === "hidden";

  function schedule() {
    if (stopped) return;
    const base = Math.min(baseOf() * Math.pow(2, failures), Math.max(maxInterval, baseOf()));
    const wait = base * (1 - jitter + Math.random() * 2 * jitter);
    clearTimeout(timer);
    timer = setTimeout(run, wait);
  }

  async function run() {
    if (stopped || running) return;
    if (hidden()) { schedule(); return; }
    running = true;
    let ok = false;
    try { ok = (await fn()) !== false; } catch { ok = false; }
    running = false;
    if (report) reportServerResult(ok);
    failures = ok ? 0 : Math.min(failures + 1, 6);
    schedule();
  }

  const onVisible = () => { if (!hidden() && !stopped) { clearTimeout(timer); run(); } };
  if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVisible);
  if (immediate) run(); else schedule();

  return () => {
    stopped = true;
    clearTimeout(timer);
    if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVisible);
  };
}

// Global network status tracker
let _onlineListeners = [];
let _isOnline = typeof navigator !== "undefined" ? navigator.onLine : true;
let _lastSaveError = null;
if (typeof window !== "undefined") {
  window.addEventListener("online", () => { _isOnline = true; _onlineListeners.forEach(fn => fn(true)); });
  window.addEventListener("offline", () => { _isOnline = false; _onlineListeners.forEach(fn => fn(false)); });
}
function useNetworkStatus() {
  const [online, setOnline] = useState(_isOnline);
  useEffect(() => {
    const handler = (v) => setOnline(v);
    _onlineListeners.push(handler);
    return () => { _onlineListeners = _onlineListeners.filter(h => h !== handler); };
  }, []);
  return online;
}

// Server time offset — corrects for student's local clock being wrong
let _serverTimeOffset = 0; // ms to ADD to Date.now() to get server time
async function syncServerTime() {
  try {
    const before = Date.now();
    const { data } = await supabase.rpc("now"); // server timestamp via public.now()
    if (!data) return; // couldn't sync — keep local time (offset stays 0)
    const after = Date.now();
    const roundTrip = (after - before) / 2;
    const serverNow = new Date(data).getTime();
    _serverTimeOffset = serverNow - after + roundTrip;
  } catch (e) {
    // Can't sync — use local time (offset stays 0)
  }
}
function serverNow() { return Date.now() + _serverTimeOffset; }
// Sync once on load
if (typeof window !== "undefined") syncServerTime();

// Clear any legacy localStorage scratch backup (state now lives server-side)
function localBackupClear(sessionId, groupName) {
  try { localStorage.removeItem(`trat-backup-${sessionId}-${groupName}`); } catch (e) {}
}

export { reportServerResult, useServerTrouble, startPolling, withRetry, useNetworkStatus, syncServerTime, serverNow, localBackupClear };
